import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useTheme } from '../../contexts/ThemeContext';

interface NavigationProps {
  currentPage: 'home' | 'crm' | 'intake' | 'closed' | 'dead-deals' | 'on-hold' | 'templates' | 'history' | 'admin';
  onLogout: () => void;
  userFullName?: string;
}

export default function Navigation({ currentPage, onLogout, userFullName: propUserFullName }: NavigationProps) {
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userFullName, setUserFullName] = useState<string>('');

  useEffect(() => {
    // If userFullName is passed as prop, use it
    if (propUserFullName) {
      setUserFullName(propUserFullName);
    } else {
      // Otherwise, load from user profile
      const loadUserProfile = async () => {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const metadata = session.user.user_metadata;
          const firstName = metadata.first_name || '';
          const lastName = metadata.last_name || '';
          setUserFullName(`${firstName} ${lastName}`.trim() || 'User');
        }
      };
      loadUserProfile();
    }
  }, [propUserFullName]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const isActive = (page: string) => currentPage === page;

  return (
    <nav className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm border-b`}>
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 flex items-center justify-center">
            <i className="ri-home-heart-line text-3xl text-navy"></i>
          </div>
          <a href="/" className={`text-xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-navy'} hover:opacity-80 transition-opacity cursor-pointer`}>
            {userFullName || 'Loans with Heena'}
          </a>
        </div>
        
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-4">
          <a 
            href="/" 
            className={`${isActive('home') ? (isDarkMode ? 'text-teal' : 'text-navy font-semibold') : (isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy')} transition-colors font-medium whitespace-nowrap cursor-pointer`}
          >
            <i className="ri-home-5-line mr-2"></i>
            Home
          </a>
          <a 
            href="/crm" 
            className={`${isActive('crm') ? (isDarkMode ? 'text-teal' : 'text-navy font-semibold') : (isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy')} transition-colors font-medium whitespace-nowrap cursor-pointer`}
          >
            <i className="ri-contacts-book-2-line mr-2"></i>
            CRM
          </a>
          <a 
            href="/intake" 
            className={`${isActive('intake') ? (isDarkMode ? 'text-teal' : 'text-navy font-semibold') : (isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy')} transition-colors font-medium whitespace-nowrap cursor-pointer`}
          >
            <i className="ri-inbox-line mr-2"></i>
            Intake
          </a>
          
          {/* Pages Dropdown */}
          <div className="relative group">
            <button className={`${['closed', 'dead-deals', 'on-hold', 'templates', 'history', 'admin'].includes(currentPage) ? (isDarkMode ? 'text-teal' : 'text-navy font-semibold') : (isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy')} transition-colors font-medium whitespace-nowrap cursor-pointer flex items-center gap-1`}>
              <i className="ri-pages-line mr-2"></i>
              Pages
              <i className="ri-arrow-down-s-line"></i>
            </button>
            <div className={`absolute top-full left-0 mt-1 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg shadow-lg py-2 min-w-[200px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50`}>
              <a 
                href="/closed" 
                className={`block px-4 py-2 ${isActive('closed') ? (isDarkMode ? 'bg-gray-700 text-teal' : 'bg-gray-50 text-navy') : (isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy')} transition-colors cursor-pointer`}
              >
                <i className="ri-archive-line mr-2"></i>
                Closed
              </a>
              <a 
                href="/dead-deals" 
                className={`block px-4 py-2 ${isActive('dead-deals') ? (isDarkMode ? 'bg-gray-700 text-teal' : 'bg-gray-50 text-navy') : (isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy')} transition-colors cursor-pointer`}
              >
                <i className="ri-close-circle-line mr-2"></i>
                Dead Deals
              </a>
              <a 
                href="/on-hold" 
                className={`block px-4 py-2 ${isActive('on-hold') ? (isDarkMode ? 'bg-gray-700 text-teal' : 'bg-gray-50 text-navy') : (isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy')} transition-colors cursor-pointer`}
              >
                <i className="ri-pause-circle-line mr-2"></i>
                On Hold
              </a>
              <a 
                href="/templates" 
                className={`block px-4 py-2 ${isActive('templates') ? (isDarkMode ? 'bg-gray-700 text-teal' : 'bg-gray-50 text-navy') : (isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy')} transition-colors cursor-pointer`}
              >
                <i className="ri-file-text-line mr-2"></i>
                Templates
              </a>
              <a 
                href="/history" 
                className={`block px-4 py-2 ${isActive('history') ? (isDarkMode ? 'bg-gray-700 text-teal' : 'bg-gray-50 text-navy') : (isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy')} transition-colors cursor-pointer`}
              >
                <i className="ri-history-line mr-2"></i>
                History
              </a>
              <a 
                href="/admin" 
                className={`block px-4 py-2 ${isActive('admin') ? (isDarkMode ? 'bg-gray-700 text-teal' : 'bg-gray-50 text-navy') : (isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy')} transition-colors cursor-pointer`}
              >
                <i className="ri-settings-3-line mr-2"></i>
                Admin
              </a>
            </div>
          </div>
          
          {/* Dark Mode Toggle */}
          <button
            onClick={toggleDarkMode}
            className={`${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium whitespace-nowrap cursor-pointer`}
            title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            <i className={`${isDarkMode ? 'ri-sun-line' : 'ri-moon-line'} text-xl`}></i>
          </button>
          
          <button
            onClick={handleLogout}
            className="text-red-600 hover:text-red-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
          >
            <i className="ri-logout-box-line mr-2"></i>
            Logout
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center text-gray-600 hover:text-navy transition-colors cursor-pointer"
        >
          <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className={`md:hidden border-t ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}
        >
          <div className="px-6 py-4 space-y-3">
            <a 
              href="/" 
              className={`block py-2 ${isActive('home') ? (isDarkMode ? 'text-teal' : 'text-navy font-semibold') : (isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy')} transition-colors font-medium cursor-pointer`}
            >
              <i className="ri-home-5-line mr-2"></i>
              Home
            </a>
            <a 
              href="/crm" 
              className={`block py-2 ${isActive('crm') ? (isDarkMode ? 'text-teal' : 'text-navy font-semibold') : (isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy')} transition-colors font-medium cursor-pointer`}
            >
              <i className="ri-contacts-book-2-line mr-2"></i>
              CRM
            </a>
            <a 
              href="/intake" 
              className={`block py-2 ${isActive('intake') ? (isDarkMode ? 'text-teal' : 'text-navy font-semibold') : (isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy')} transition-colors font-medium cursor-pointer`}
            >
              <i className="ri-inbox-line mr-2"></i>
              Intake
            </a>
            <a 
              href="/closed" 
              className={`block py-2 ${isActive('closed') ? (isDarkMode ? 'text-teal' : 'text-navy font-semibold') : (isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy')} transition-colors font-medium cursor-pointer`}
            >
              <i className="ri-archive-line mr-2"></i>
              Closed
            </a>
            <a 
              href="/dead-deals" 
              className={`block py-2 ${isActive('dead-deals') ? (isDarkMode ? 'text-teal' : 'text-navy font-semibold') : (isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy')} transition-colors font-medium cursor-pointer`}
            >
              <i className="ri-close-circle-line mr-2"></i>
              Dead Deals
            </a>
            <a 
              href="/on-hold" 
              className={`block py-2 ${isActive('on-hold') ? (isDarkMode ? 'text-teal' : 'text-navy font-semibold') : (isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy')} transition-colors font-medium cursor-pointer`}
            >
              <i className="ri-pause-circle-line mr-2"></i>
              On Hold
            </a>
            <a 
              href="/templates" 
              className={`block py-2 ${isActive('templates') ? (isDarkMode ? 'text-teal' : 'text-navy font-semibold') : (isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy')} transition-colors font-medium cursor-pointer`}
            >
              <i className="ri-file-text-line mr-2"></i>
              Templates
            </a>
            <a 
              href="/history" 
              className={`block py-2 ${isActive('history') ? (isDarkMode ? 'text-teal' : 'text-navy font-semibold') : (isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy')} transition-colors font-medium cursor-pointer`}
            >
              <i className="ri-history-line mr-2"></i>
              History
            </a>
            <a 
              href="/admin" 
              className={`block py-2 ${isActive('admin') ? (isDarkMode ? 'text-teal' : 'text-navy font-semibold') : (isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy')} transition-colors font-medium cursor-pointer`}
            >
              <i className="ri-settings-3-line mr-2"></i>
              Admin
            </a>
            <button
              onClick={toggleDarkMode}
              className={`block w-full text-left py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}
            >
              <i className={`${isDarkMode ? 'ri-sun-line' : 'ri-moon-line'} mr-2`}></i>
              {isDarkMode ? 'Light Mode' : 'Dark Mode'}
            </button>
            <button
              onClick={handleLogout}
              className="block w-full text-left py-2 text-red-600 hover:text-red-700 transition-colors font-medium cursor-pointer"
            >
              <i className="ri-logout-box-line mr-2"></i>
              Logout
            </button>
          </div>
        </motion.div>
      )}
    </nav>
  );
}
